import type { SimulationClockConfig } from "../config/gameplay-config";

export interface MonotonicTimeSource {
  now(): number;
}

export interface SimulationClockSnapshot {
  readonly backgrounded: boolean;
  readonly elapsedMs: number;
  readonly paused: boolean;
  readonly remainderMs: number;
  readonly stepCount: number;
}

interface SimulationClockOptions {
  readonly config: SimulationClockConfig;
  readonly onFixedStep: (stepMs: number) => void;
  readonly timeSource: MonotonicTimeSource;
}

const validatePositiveFinite = (value: number, name: string): void => {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be positive and finite`);
  }
};

const validateNonNegativeFinite = (value: number, name: string): void => {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be non-negative and finite`);
  }
};

export class SimulationClock {
  private accumulatorMs = 0;
  private backgrounded = false;
  private elapsedMs = 0;
  private lastRealSampleMs: number | undefined;
  private readonly maximumRealFrameDeltaMs: number;
  private paused = false;
  private stopped = false;
  private readonly fixedStepMs: number;
  private readonly onFixedStep: (stepMs: number) => void;
  private stepCount = 0;
  private readonly timeSource: MonotonicTimeSource;

  public constructor(options: SimulationClockOptions) {
    validatePositiveFinite(options.config.fixedStepMs, "Fixed step");
    validatePositiveFinite(
      options.config.maximumRealFrameDeltaMs,
      "Maximum real frame delta",
    );
    if (options.config.maximumRealFrameDeltaMs < options.config.fixedStepMs) {
      throw new RangeError(
        "Maximum real frame delta must not be below the fixed step",
      );
    }
    this.fixedStepMs = options.config.fixedStepMs;
    this.maximumRealFrameDeltaMs = options.config.maximumRealFrameDeltaMs;
    this.onFixedStep = options.onFixedStep;
    this.timeSource = options.timeSource;
  }

  public advanceControlled(milliseconds: number): number {
    validateNonNegativeFinite(milliseconds, "Controlled time");
    if (this.isBlocked() || milliseconds === 0) {
      return 0;
    }
    this.lastRealSampleMs = undefined;
    return this.consume(milliseconds);
  }

  public background(): void {
    if (!this.backgrounded) {
      this.backgrounded = true;
    }
  }

  public foreground(): void {
    if (this.backgrounded) {
      this.backgrounded = false;
      if (!this.paused) {
        this.lastRealSampleMs = undefined;
      }
    }
  }

  public getSnapshot(): SimulationClockSnapshot {
    return {
      backgrounded: this.backgrounded,
      elapsedMs: this.elapsedMs,
      paused: this.paused,
      remainderMs: this.accumulatorMs,
      stepCount: this.stepCount,
    };
  }

  public pause(): void {
    if (!this.paused && !this.stopped) {
      this.paused = true;
    }
  }

  public resume(): void {
    if (this.paused && !this.stopped) {
      this.paused = false;
      if (!this.backgrounded) {
        this.lastRealSampleMs = undefined;
      }
    }
  }

  public stop(): boolean {
    if (this.stopped) {
      return false;
    }
    this.stopped = true;
    this.paused = false;
    this.accumulatorMs %= this.fixedStepMs;
    return true;
  }

  public sampleRealTime(): number {
    return this.sampleTime(this.timeSource.now());
  }

  public sampleTime(sampleMs: number): number {
    validateNonNegativeFinite(sampleMs, "Real time sample");
    if (
      this.lastRealSampleMs !== undefined &&
      sampleMs < this.lastRealSampleMs
    ) {
      throw new RangeError("Real time sample must be monotonic");
    }
    const previousSampleMs = this.lastRealSampleMs;
    this.lastRealSampleMs = sampleMs;
    if (previousSampleMs === undefined || this.isBlocked()) {
      return 0;
    }
    return this.consume(
      Math.min(sampleMs - previousSampleMs, this.maximumRealFrameDeltaMs),
    );
  }

  public resetRealTimeSample(): void {
    this.lastRealSampleMs = undefined;
  }

  private consume(milliseconds: number): number {
    this.accumulatorMs += milliseconds;
    let steps = 0;
    while (this.accumulatorMs >= this.fixedStepMs && !this.isBlocked()) {
      this.accumulatorMs -= this.fixedStepMs;
      this.elapsedMs += this.fixedStepMs;
      this.stepCount += 1;
      steps += 1;
      this.onFixedStep(this.fixedStepMs);
    }
    if (this.stopped) {
      this.accumulatorMs %= this.fixedStepMs;
    }
    return steps;
  }

  private isBlocked(): boolean {
    return this.paused || this.backgrounded || this.stopped;
  }
}
